"use client";

// Root-level error boundary. Catches render-phase throws that bubble all the way
// up — including ones thrown by the providers in app/layout.tsx (ClerkProvider,
// I18nProvider, etc.). Replaces the generic Next.js "Application error: a
// client-side exception" page, and (critically) reports the stack back to us so
// crashes we can't reproduce locally (e.g. a China user's browser) become visible.
//
// Deliberately self-contained: no provider/i18n/Tailwind dependency, because the
// thing that crashed may BE one of those.

import { useEffect, useState, type CSSProperties } from "react";
import { isVersionSkewError, recoverFromVersionSkew } from "@/lib/version-skew";

// Same error → at most one report per 6h per browser. This is what stops a user
// who reloads on the error page (we saw 4 reloads in 3.5min) from spamming.
const REPORT_TTL_MS = 6 * 60 * 60 * 1000;
const MAX_STORED_SIGS = 20;

function reportOnce(error: Error & { digest?: string }) {
  try {
    const sig = (error?.digest || error?.message || "unknown").slice(0, 200);
    const now = Date.now();

    let store: Record<string, number> = {};
    try {
      store = JSON.parse(localStorage.getItem("ce_sent") || "{}");
    } catch {
      store = {};
    }

    // Client-side dedup: skip if we already reported this error recently.
    if (store[sig] && now - store[sig] < REPORT_TTL_MS) return;

    store[sig] = now;
    // Keep the dedup store bounded.
    const keys = Object.keys(store);
    if (keys.length > MAX_STORED_SIGS) {
      keys
        .sort((a, b) => store[a] - store[b])
        .slice(0, keys.length - MAX_STORED_SIGS)
        .forEach((k) => delete store[k]);
    }
    try {
      localStorage.setItem("ce_sent", JSON.stringify(store));
    } catch {
      // ignore quota / private-mode errors
    }

    const payload = JSON.stringify({
      msg: (error?.message || "").slice(0, 500),
      digest: error?.digest || null,
      stack: (error?.stack || "").slice(0, 4000),
      ua: navigator.userAgent,
      lang: navigator.language,
      url: location.href,
      ts: now,
    });

    // Fire-and-forget; must never block render or throw.
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(
        "/api/client-error",
        new Blob([payload], { type: "application/json" })
      );
    } else {
      fetch("/api/client-error", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Reporting itself must never crash the error page.
  }
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  // A Server Action ID mismatch or a chunk-load failure thrown during render
  // bubbles to this boundary rather than to window.onerror, so the client-side
  // recovery listener never sees it. Catch it here too: recover instead of
  // showing the crash page. Best-effort — SSR-thrown server errors arrive
  // redacted (opaque digest, generic message) and won't classify as skew.
  const skew = isVersionSkewError(error?.message || error?.digest || "");

  // Blank the page only once a reload is actually in flight. Gating the blank
  // render on `skew` alone would strand the user on a permanent empty page
  // whenever recovery is refused (reload cooldown active, sessionStorage
  // unavailable, offline) — those cases must fall through to the crash UI.
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (skew && recoverFromVersionSkew()) {
      setReloading(true);
    }
    // Report even when recovering: "Loading chunk failed" can also mean a bad
    // build or a blocked CDN — failures a reload does not fix — so skipping
    // these would hide them from the client-error logs entirely. reportOnce's
    // 6h dedup bounds the noise, and sendBeacon/keepalive lets the report
    // survive the reload navigation.
    reportOnce(error);
  }, [error, skew]);

  if (reloading) {
    return (
      <html>
        <body style={bodyStyle} />
      </html>
    );
  }

  return (
    <html>
      <body style={bodyStyle}>
        <div style={wrapStyle}>
          <h2 style={titleStyle}>Something went wrong</h2>
        </div>
      </body>
    </html>
  );
}

const bodyStyle: CSSProperties = {
  margin: 0,
  fontFamily: "system-ui, -apple-system, sans-serif",
  background: "#ffffff",
};
const wrapStyle: CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const titleStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  margin: 0,
  color: "#111111",
};
